// WHAT: Validation utility functions for Miracle Flow inputs
// WHY:  Provides validation for affirmations, journal entries, gratitude items, images, and timers
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: None
// RISKS:
//   - NOTE: Validation rules may not match backend validation
//   - NOTE: Character limits are hardcoded - may need adjustment

/**
 * WHAT:
 *   Utility functions for validating various input types including affirmations,
 *   journal entries, gratitude items, image files, and timer durations.
 *
 * WHY:
 *   Ensures data integrity and provides user-friendly error messages.
 *
 * BEHAVIOR:
 *   - Validates affirmation text (length, empty)
 *   - Validates journal entries (length, empty)
 *   - Validates gratitude items (count, length, empty)
 *   - Validates image files (type, size)
 *   - Validates timer durations (min/max)
 *
 * RISKS:
 *   - NOTE: Validation rules may not match backend validation
 *   - NOTE: Character limits are hardcoded - may need adjustment
 */

/**
 * WHAT:
 *   Validates affirmation text.
 *
 * WHY:
 *   Ensures affirmations meet length requirements.
 *
 * @param text
 *
 * @returns Validation result with isValid flag and optional error message
 *
 */
export const validateAffirmation = (text: string): { isValid: boolean; error?: string } => {
  if (!text.trim()) {
    return { error: "Affirmation cannot be empty", isValid: false };
  }

  if (text.length < 10) {
    return {
      error: "Affirmation must be at least 10 characters long",
      isValid: false,
    };
  }

  if (text.length > 500) {
    return {
      error: "Affirmation must be less than 500 characters",
      isValid: false,
    };
  }

  return { isValid: true };
};

export const validateJournalEntry = (text: string): { isValid: boolean; error?: string } => {
  if (!text.trim()) {
    return { error: "Entry cannot be empty", isValid: false };
  }

  if (text.length > 1000) {
    return { error: "Entry must be less than 1000 characters", isValid: false };
  }

  return { isValid: true };
};

export const validateGratitudeItems = (items: string[]): { isValid: boolean; error?: string } => {
  if (items.length === 0) {
    return { error: "At least one gratitude item is required", isValid: false };
  }

  if (items.length > 5) {
    return { error: "Maximum 5 gratitude items allowed", isValid: false };
  }

  for (const item of items) {
    if (!item.trim()) {
      return { error: "Gratitude items cannot be empty", isValid: false };
    }

    if (item.length > 200) {
      return {
        error: "Each gratitude item must be less than 200 characters",
        isValid: false,
      };
    }
  }

  return { isValid: true };
};

export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

  if (!allowedTypes.includes(file.type)) {
    return {
      error: "Only JPEG, PNG, WebP, and GIF files are allowed",
      isValid: false,
    };
  }

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { error: "File size must be less than 10MB", isValid: false };
  }

  return { isValid: true };
};

export const validateTimerDuration = (seconds: number): { isValid: boolean; error?: string } => {
  if (seconds < 30) {
    return { error: "Timer must be at least 30 seconds", isValid: false };
  }

  if (seconds > 7200) {
    // 2 hours
    return { error: "Timer cannot exceed 2 hours", isValid: false };
  }

  return { isValid: true };
};
